import React from 'react'
import { NextSeo, BreadcrumbJsonLd } from 'next-seo'
import gql from 'graphql-tag'
import coverImages from '~/lib/cover-images'
import { LISTING_VIEWED } from '../lib/tracking-events'
import Error from './_error'
import { getCategories, getLinkProps } from '../utils'
import { withTranslation } from '~/i18n'
import ListingPageContent from '~/components/ListingPageContent'
import AdminMenu from '../components/AdminMenu/dynamic'
import Testimonials from '../components/Testimonials'
import SiteFooter from '../components/SiteFooter'
import FeedbackWidget from '../components/FeedbackWidget'
import CustomPageTracking from '../components/CustomPageTracking'
import { decodeGlobalId } from '~/utils/id'
import getViewer from '~/lib/get-viewer'
import { captureException } from '~/lib/sentry'

const GET_CART_AND_LISTING = gql`
  query getCartAndListing($id: ID!, $hash: String!, $isLoggedIn: Boolean!) {
    viewer @include(if: $isLoggedIn) {
      id
      cart {
        id
        tickets {
          id
        }
      }
    }
    listing(id: $id, hash: $hash) {
      id
      hash
      status
      description
      tickets(first: 99) {
        edges {
          node {
            id
            status
            seating {
              id
              entrance
              section
              row
              seat
            }
          }
        }
      }
      event {
        __typename
        id
        slug
        name
        startDate
        endDate
        isPopular
        wantedListingsCount
        headerImageUrl
        imageUrl
        facebookEventWalls {
          facebookUrl
          isMainEventWall
        }
        location {
          id
          slug
          name
          background
          zipcode
          city {
            id
            slug
            name
          }
        }
        category
        instagramUsername
        types(first: 99) {
          edges {
            node {
              id
              slug
            }
          }
        }
        warning {
          title
          message
          url {
            text
            url
          }
        }
      }
      eventType {
        id
        slug
        title
        startDate
        endDate
        isExpired
        wantedListingsCount
        availableTicketsCount
      }
      isSecureSwapAvailable
      seller {
        id
        firstname
        lastname
        avatar
        avatarBig
        city
        isPhoneVerified
        successfullySoldCount
        facebookAccount {
          friendsCount
        }
      }
      numberOfTicketsStillForSale
      numberOfTicketsInListing
      price {
        sellerPrice {
          formattedAmount
          symbol
          amount
          precision
          thousandSeparator
          decimalSeparator
          symbolPlacement
        }
        serviceFeeForBuyer {
          formattedAmount
          symbol
          amount
          precision
          thousandSeparator
          decimalSeparator
          symbolPlacement
        }
        transactionFeePerTicket {
          formattedAmount
          symbol
          amount
          precision
          thousandSeparator
          decimalSeparator
          symbolPlacement
        }
        totalPriceWithTransactionFee {
          formattedAmount
          symbol
          amount
          precision
          thousandSeparator
          decimalSeparator
          symbolPlacement
        }
        originalPrice {
          formattedAmount
        }
      }
    }
  }
`

const Listing = ({ t, id, hash, query, data, randomCover, adminUrl }) => {
  if (!data.listing) {
    return <Error statusCode={404} />
  }
  const { listing = {} } = data
  const { event = {}, eventType = {} } = listing

  // Create Breadcrumbs array based on existing event and eventTypes info
  const categoryList = getCategories(t)
  const eventCategory = event.category
    ? categoryList.find(el => el.value === event.category)
    : null
  const breadcrumbs = [
    ...(eventCategory
      ? [
          {
            position: 1,
            name: eventCategory?.name,
            item: getLinkProps('CATEGORY', { slug: eventCategory.slug }).as,
          },
        ]
      : []),
    ...(event?.location?.city
      ? [
          {
            position: 2,
            name: event.location.city.name,
            item: getLinkProps('CITY', {
              slug: event.location.city.slug,
              id: decodeGlobalId(event.location.city.id),
            }).as,
          },
        ]
      : []),
    ...(event?.location?.name
      ? [
          {
            position: 3,
            name: event.location.name,
            item: getLinkProps('LOCATION', {
              slug: event.location.slug,
              id: decodeGlobalId(event.location.id),
            }).as,
          },
        ]
      : []),
    ...(event.name
      ? [
          {
            position: 4,
            name: event.name,
            item: getLinkProps('EVENT_PAGE', {
              eventSlug: event.slug,
              eventId: decodeGlobalId(event.id),
            }).as,
          },
        ]
      : []),
    ...(eventType
      ? [
          {
            position: 5,
            name: eventType.title,
            item: getLinkProps('EVENT_TYPE_PAGE', {
              eventSlug: event.slug,
              eventId: decodeGlobalId(event.id),
              eventTypeSlug: eventType.slug,
              eventTypeId: decodeGlobalId(eventType.id),
            }).as,
          },
        ]
      : []),
    ...(listing.id
      ? [
          {
            position: 6,
            name: t('listing:purchaser.listing'),
            item: getLinkProps('LISTING_PAGE', {
              slug: event.slug,
              id: decodeGlobalId(listing.id),
              hash: listing.hash,
            }).as,
          },
        ]
      : []),
  ]

  return (
    <>
      <CustomPageTracking
        handle={LISTING_VIEWED}
        payload={{
          id: decodeGlobalId(listing.id),
          eventId: decodeGlobalId(event.id),
          eventName: event.name,
          ...(eventType
            ? {
                eventTypeId: decodeGlobalId(eventType.id),
                eventTypeName: eventType.title,
              }
            : {}),
          availableTickets: listing.numberOfTicketsStillForSale,
        }}
      />
      <NextSeo
        title={`${event.name} – ${t('common:site.shortTitle')} – ${t(
          'common:site.name'
        )}`}
        description={event.description || t('common:site.description')}
        openGraph={{
          title: event.name,
          description: event.description || t('common:site.description'),
          site_name: t('common:site.name'),
        }}
      />
      <BreadcrumbJsonLd itemListElements={breadcrumbs} />
      <ListingPageContent
        id={id}
        data={data}
        hash={hash}
        randomCover={randomCover}
      />
      <AdminMenu links={[{ text: 'Open listing', url: adminUrl }]} />
      <FeedbackWidget />
      <Testimonials />
      <SiteFooter />
    </>
  )
}

Listing.defaultProps = {
  data: {},
}

Listing.getInitialProps = async ({ req, query, apolloClient }) => {
  const viewer = await getViewer(apolloClient, req)
  const isLoggedIn = Boolean(viewer)
  const { id, hash: queryHash, slug } = query
  let data = {}
  // Sometimes the UTM querystring is attached wrongfully to the url.
  // The hash is always the first 10 chars in this case.
  const hash = queryHash.substring(0, 10)

  try {
    const res = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: GET_CART_AND_LISTING,
      variables: { id, hash, isLoggedIn },
    })
    data = res.data
  } catch (error) {
    if (!error.message.includes('Could not find Listing')) {
      captureException(error, 'listing-page', {
        extra: {
          id,
          hash,
          slug,
        },
      })
    }
  }

  const randomCover =
    coverImages.default[Math.floor(Math.random() * coverImages.default.length)]

  return {
    data,
    namespacesRequired: ['common', 'listing', 'event-eventtype'],
    eventSlug: slug,
    id,
    hash,
    adminUrl: `/ads/show/${id}`,
    randomCover,
  }
}

export default withTranslation(['common', 'listing', 'event-eventtype'])(
  Listing
)
